import type {
  AdminUserDetail,
  AuditRecordBrief,
  BillingEventBrief,
  SummaryJobBrief,
  UserStatus,
} from "../../api/admin-users";
import { t } from "../../i18n";
import { s } from "./user-detail-styles";

const TAB_KEYS = [
  "user_detail.tab_overview",
  "user_detail.tab_summary_jobs",
  "user_detail.tab_audit",
  "user_detail.tab_billing",
] as const;
const TAB_VALUES = ["Overview", "Summary Jobs", "Audit", "Billing"] as const;
export type UserDetailTab = (typeof TAB_VALUES)[number];

export function InfoCard({
  user,
}: {
  user: AdminUserDetail;
}): React.JSX.Element {
  const mono = { ...s.infoValue, fontFamily: "var(--font-mono)", fontSize: 12 };
  return (
    <div style={s.card}>
      <InfoRow label={t("user_detail.user_id")} value={user.id} mono />
      <InfoRow
        label={t("user_detail.custom_id")}
        value={user.custom_id ?? "-"}
        mono
      />
      <InfoRow
        label={t("user_detail.status")}
        value={<StatusBadge status={user.status} />}
      />
      <InfoRow
        label={t("user_detail.created")}
        value={formatDate(user.created_at)}
      />
    </div>
  );

  function InfoRow({
    label,
    value,
    mono: useMono,
  }: {
    label: string;
    value: React.ReactNode;
    mono?: boolean;
  }): React.JSX.Element {
    return (
      <div style={s.infoRow}>
        <span style={s.infoLabel}>{label}</span>
        <span style={useMono ? mono : s.infoValue}>{value}</span>
      </div>
    );
  }
}

export function UserDetailTabBar({
  active,
  onChange,
}: {
  active: UserDetailTab;
  onChange: (tab: UserDetailTab) => void;
}): React.JSX.Element {
  return (
    <div style={s.tabBar}>
      {TAB_VALUES.map((tab, index) => (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(tab)}
          style={tab === active ? { ...s.tab, ...s.tabActive } : s.tab}
        >
          {t(TAB_KEYS[index])}
        </button>
      ))}
    </div>
  );
}

export function UserDetailTabContent({
  tab,
  user,
}: {
  tab: UserDetailTab;
  user: AdminUserDetail;
}): React.JSX.Element {
  if (tab === "Overview") return <OverviewTab user={user} />;
  if (tab === "Summary Jobs") {
    return <JobsTab jobs={user.recent_summary_jobs} />;
  }
  if (tab === "Audit") {
    return <AuditTab records={user.recent_audit_records} />;
  }
  return <BillingTab events={user.recent_billing_events} />;
}

function OverviewTab({ user }: { user: AdminUserDetail }): React.JSX.Element {
  return (
    <div style={s.grid}>
      {overviewRows(user).map(([label, value]) => (
        <div key={label} style={s.statCard}>
          <span style={s.statLabel}>{label}</span>
          <span style={s.statValue}>{value}</span>
        </div>
      ))}
    </div>
  );
}

function JobsTab({
  jobs,
}: {
  jobs: ReadonlyArray<SummaryJobBrief>;
}): React.JSX.Element {
  if (jobs.length === 0) {
    return <p style={s.emptyTab}>{t("user_detail.no_summary_jobs")}</p>;
  }
  return (
    <table style={s.miniTable}>
      <thead>
        <tr>
          <th style={s.th}>{t("user_detail.col_status")}</th>
          <th style={s.th}>{t("user_detail.col_target")}</th>
          <th style={s.th}>{t("user_detail.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map((job) => (
          <tr key={job.id}>
            <td style={s.td}>{job.status}</td>
            <td style={s.td}>{job.target_type}</td>
            <td style={s.td}>{formatDate(job.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AuditTab({
  records,
}: {
  records: ReadonlyArray<AuditRecordBrief>;
}): React.JSX.Element {
  if (records.length === 0) {
    return <p style={s.emptyTab}>{t("user_detail.no_audit_records")}</p>;
  }
  return (
    <table style={s.miniTable}>
      <thead>
        <tr>
          <th style={s.th}>{t("user_detail.col_action")}</th>
          <th style={s.th}>{t("user_detail.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {records.map((record) => (
          <tr key={record.id}>
            <td style={s.td}>{record.action}</td>
            <td style={s.td}>{formatDate(record.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BillingTab({
  events,
}: {
  events: ReadonlyArray<BillingEventBrief>;
}): React.JSX.Element {
  if (events.length === 0) {
    return <p style={s.emptyTab}>{t("user_detail.no_billing_events")}</p>;
  }
  return (
    <table style={s.miniTable}>
      <thead>
        <tr>
          <th style={s.th}>{t("user_detail.col_type")}</th>
          <th style={s.th}>{t("user_detail.col_amount")}</th>
          <th style={s.th}>{t("user_detail.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event.id}>
            <td style={s.td}>{event.entry_type}</td>
            <td style={{ ...s.td, textAlign: "right" }}>
              {event.amount.toLocaleString()}
            </td>
            <td style={s.td}>{formatDate(event.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({ status }: { status: UserStatus }): React.JSX.Element {
  const color = STATUS_COLOR[status];
  return (
    <span style={{ ...s.badge, background: color.bg, color: color.fg }}>
      {capitalize(status)}
    </span>
  );
}

function overviewRows(
  user: AdminUserDetail,
): ReadonlyArray<readonly [string, string]> {
  return [
    [t("user_detail.plan"), capitalize(user.plan_type)],
    [
      t("user_detail.token_balance"),
      user.topup_tokens_remaining.toLocaleString(),
    ],
    [
      t("user_detail.storage"),
      formatStorage(user.storage_used_bytes, user.storage_limit_bytes),
    ],
    [t("user_detail.devices"), String(user.device_count)],
  ];
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatStorage(used: number, limit: number): string {
  const fmt = (bytes: number): string => {
    if (bytes >= 1_073_741_824) {
      return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
    }
    if (bytes >= 1_048_576) {
      return `${(bytes / 1_048_576).toFixed(1)} MB`;
    }
    return `${(bytes / 1_024).toFixed(0)} KB`;
  };
  return limit > 0 ? `${fmt(used)} / ${fmt(limit)}` : fmt(used);
}

const STATUS_COLOR: Record<UserStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  frozen: { bg: "#fef2f2", fg: "#991b1b" },
  deactivated: { bg: "#f3f4f6", fg: "#6b7280" },
};
